import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Spin, Image, message, Select, Card } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { DndProvider, useDrag } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const { Option } = Select;

const ItemTypes = {
  MEMBER: 'member',
};
const PAGE_SIZE = 999;

const DraggableMember = ({ member, onDragEnd, onSelect, isActive }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.MEMBER,
    item: { member },
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (item && dropResult) {
        onDragEnd(item.member, dropResult);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const handleClick = () => {
    if (onSelect) {
      onSelect(member);
    }
  };

  const getDeliveryIcon = (deliveryType) => {
    if (deliveryType === 'SVIP专属' || deliveryType === '一对一') {
      return '👑';
    } else if (deliveryType === 'VIP私教' || deliveryType === '一对二') {
      return '⭐';
    } else if (deliveryType === '私教小团' || deliveryType === '一对多') {
      return '👥';
    }
    return null;
  };

  return (
    <Card
      ref={drag}
      onClick={handleClick}
      size="small"
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'pointer',
        width: 'calc(33.333% - 16px)',
        margin: 8,
        border: `1px solid ${isActive ? '#1677ff' : '#d9d9d9'}`,
        backgroundColor: isActive ? '#e6f4ff' : '#fff',
        boxShadow: isActive ? '0 0 0 2px rgba(22,119,255,0.15)' : 'none',
        padding: '8px',
        textAlign: 'center',
      }}
    >
      <Image
        src={member.photoAddress}
        alt={member.babyName}
        preview={false}
        style={{ 
          width: '100%', 
          maxWidth: 80, 
          height: 'auto', 
          aspectRatio: '1', 
          borderRadius: '50%', 
          marginBottom: 8,
          objectFit: 'cover'
        }}
        fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvcnQtc2l6ZT0iMTIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiNmZmYiPk1lbWJlcjwvdGV4dD48L3N2Zz4="
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {getDeliveryIcon(member.deliveryType) && <span style={{ marginRight: 4 }}>{getDeliveryIcon(member.deliveryType)}</span>}
        {member.deliveryType && <span style={{ marginRight: 4, fontSize: '12px', color: '#666' }}>{member.deliveryType}</span>}
        <span>{member.babyName}</span>
      </div>
    </Card>
  );
};

const MemberSelector = ({ onSelect, selectedMemberIds = [] }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState();
  const [selectedTeam, setSelectedTeam] = useState();
  const { apiClient, user } = useAuth();
  const hasLoadedSchoolsRef = useRef(false);

  const storedUser = useMemo(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      const cache = localStorage.getItem('user');
      return cache ? JSON.parse(cache) : {};
    } catch (error) {
      console.warn('Failed to parse user from localStorage', error);
      return {};
    }
  }, []);

  const currentCustomerId = storedUser?.customerId || user?.customerId;

  const resetMembersState = useCallback(() => {
    setMembers([]);
  }, []);

  const fetchTeams = useCallback(async (schoolId) => {
    if (!apiClient || !schoolId) {
      setTeamOptions([]);
      return;
    }
    try {
      const response = await apiClient.post(`/sports-server-prod/api.v1/customer/queryClassList?customerId=${schoolId}`);
      if (response.data?.code === 200) {
        setTeamOptions(response.data.data.list || []);
      } else {
        message.error(response.data?.msg || '获取小队失败');
      }
    } catch (error) {
      console.error('获取小队列表失败:', error);
      message.error('获取小队失败');
    }
  }, [apiClient]);

  const fetchMembers = useCallback(async ({
    schoolId,
    teamId,
  } = {}) => {
    if (!apiClient) return;

    const resolvedSchoolId = schoolId ?? selectedSchool;
    if (!resolvedSchoolId) return;

    const resolvedTeamId = typeof teamId !== 'undefined' ? teamId : selectedTeam;

    setLoading(true);
    try {
      const params = {
        pageNum: 1,
        pageSize: PAGE_SIZE,
        customerId: resolvedSchoolId,
        classId: resolvedTeamId,
      };

      const response = await apiClient.get('/sports-base-server-prod/main/getBabyList', { params });

      if (response.data && response.data.result === '1') {
        const list = response.data.babyList || [];
        setMembers(list);
      } else {
        message.error(response.data?.msg || '获取会员列表失败');
      }
    } catch (error) {
      console.error('获取会员列表错误:', error);
      message.error('获取会员列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [apiClient, selectedSchool, selectedTeam]);

  const handleSchoolChange = useCallback((value) => {
    setSelectedSchool(value);
    setSelectedTeam(undefined);
    setTeamOptions([]);
    resetMembersState();

    if (!value) {
      return;
    }

    fetchTeams(value);
    fetchMembers({ schoolId: value });
  }, [fetchMembers, fetchTeams, resetMembersState]);

  const handleTeamChange = (value) => {
    setSelectedTeam(value);
    resetMembersState();

    if (!selectedSchool) {
      return;
    }

    fetchMembers({ schoolId: selectedSchool, teamId: value });
  };

  useEffect(() => {
    if (!apiClient || hasLoadedSchoolsRef.current) return;
    hasLoadedSchoolsRef.current = true;

    const fetchSchools = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get('/sports-base-server-prod/myPage/searchCustomer', {
          params: { pageNum: 1, pageSize: 999 },
        });
        if (response.data?.result === '1') {
          const fullCustomerList = response.data.customerList || [];

          // 根据权限过滤校区列表（与VenueDashboard/VenueMonitor保持一致）
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          let filteredCustomers = [];

          if (currentUser.role === 'R00001') {
            // 角色为 R00001 时，可以看到所有校区
            filteredCustomers = fullCustomerList;
          } else {
            // 检查当前用户是否在任何校区的 phoneDecimal 中，或者 customerId 是否匹配
            filteredCustomers = fullCustomerList.filter(customer => {
              // 检查 customerId 是否匹配当前用户
              if (customer.customerId === currentUser.customerId) {
                return true;
              }

              // 检查当前用户的各种可能标识是否在 phoneDecimal 中 (精确匹配)
              if (customer.phoneDecimal) {
                const phoneDecimals = customer.phoneDecimal.split(';').map(item => item.trim()).filter(item => item);

                // 检查多种可能的用户标识
                const userIdentifiers = [
                  currentUser.userName,     // 用户名
                  currentUser.name,         // 姓名
                  currentUser.userId,       // 用户ID
                  currentUser.username,     // 登录用户名
                ].filter(id => id); // 过滤掉undefined或null值

                // 检查是否有任何一个用户标识存在于phoneDecimal中
                return userIdentifiers.some(identifier =>
                  phoneDecimals.includes(identifier)
                );
              }

              return false;
            });
          }

          setSchoolOptions(filteredCustomers);

          // 使用当前用户的默认校区初始化选择
          const initialCustomerId = currentCustomerId || currentUser.customerId;
          if (initialCustomerId) {
            const exists = filteredCustomers.some(school => school.customerId === initialCustomerId);
            if (exists) {
              handleSchoolChange(initialCustomerId);
            }
          }
        }
      } catch (error) {
        console.error('获取校区列表错误:', error);
        message.error('获取校区列表失败');
      } finally {
        setLoading(false);
      }
    };

    fetchSchools();
  }, [apiClient, currentCustomerId, handleSchoolChange]);


  const handleDragEnd = (member, dropResult) => {
    if (onSelect) {
      onSelect(member, dropResult);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Select
          placeholder="请选择校区"
          value={selectedSchool}
          onChange={handleSchoolChange}
          loading={loading}
          allowClear
          disabled={schoolOptions.length <= 1}
          style={{ width: '48%', marginRight: '4%' }}
          dropdownStyle={{ maxWidth: '300px' }}
          optionLabelProp="label"
        >
          {schoolOptions.map(school => (
            <Option key={school.customerId} value={school.customerId} label={school.customerName}>
              {school.customerName}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="请选择小队"
          value={selectedTeam}
          onChange={handleTeamChange}
          loading={loading}
          allowClear
          style={{ width: '48%' }}
        >
          {teamOptions.map(team => (
            <Option key={team.classId} value={team.classId}>
              {team.className}
            </Option>
          ))}
        </Select>
      </div>
      <DndProvider backend={HTML5Backend}>
        <div
          style={{
            height: 'auto',
            minHeight: members.length > 0 ? 'auto' : '200px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '8px 16px',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
            {members.map(member => (
              <DraggableMember
                key={member.id}
                member={member}
                onDragEnd={handleDragEnd}
                onSelect={onSelect}
                isActive={selectedMemberIds.includes(member.id)}
              />
            ))}
          </div>
          {loading && members.length === 0 && <Spin />}
          {!loading && members.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              暂无会员数据
            </div>
          )}
        </div>
      </DndProvider>
    </div>
  );
};

export default MemberSelector;
